import { useState, Dispatch, SetStateAction } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { IoEyeOutline, IoEyeOffOutline } from 'react-icons/io5';
import { Input } from '../ui/input';
import { signIn } from '@aws-amplify/auth';
import { useToast } from '@/hooks/use-toast';
import { validateEmail } from './helpers';
import StyledButton from '../StyledHtmlElements/Button';

const formSchema = z.object({
  email: z
    .string()
    .min(2, {
      message: 'Die Email muss mindestens 2 Zeichen lang sein.',
    })
    .max(50, {
      message: 'Die Email darf maximal 50 Zeichen lang sein.',
    })
    .refine(validateEmail, {
      message: 'Keine gültige Email-Adresse',
    }),
  password: z.string(),
});

type Props = {
  setIsForcePwChange: Dispatch<SetStateAction<boolean>>;
  setForgotPasswortEmail: Dispatch<SetStateAction<string>>;
};

const LoginInForm = ({ setIsForcePwChange, setForgotPasswortEmail }: Props) => {
  const [showPasswort, setShowPasswort] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setLoading(true);
    const email = values.email.trim();
    const password = values.password.trim();

    try {
      const { nextStep } = await signIn({ username: email, password });
      if ('signInStep' in nextStep && nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setIsForcePwChange(true);
        setForgotPasswortEmail(email);
        return;
      }
    } catch (error: unknown) {
      let message: string = 'Ein Fehler ist aufgetreten';

      if (error && typeof error === 'object' && 'message' in error) {
        message = error.message as string;
      }

      toast({
        variant: 'destructive',
        title: 'Fehler beim Anmelden',
        description: message,
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className=" font-heading text-center tracking-widest text-xl">Anmeldung</h1>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} autoComplete="on" className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor="sign-in-email">Email</FormLabel>
                <FormControl>
                  <Input
                    id="sign-in-email"
                    placeholder="Geben Sie Ihre E-Mail-Adresse ein"
                    type="email"
                    autoComplete="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor="sign-in-password">Passwort</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      id="sign-in-password"
                      placeholder="Geben Sie Ihr Passwort ein"
                      type={showPasswort ? 'text' : 'password'}
                      {...field}
                    />
                    {showPasswort ? (
                      <IoEyeOutline
                        onClick={() => setShowPasswort(false)}
                        className="absolute right-3 top-[50%] -translate-y-1/2 opacity-75 "
                        size={20}
                      />
                    ) : (
                      <IoEyeOffOutline
                        onClick={() => setShowPasswort(true)}
                        className="absolute right-3 top-[50%] -translate-y-1/2 opacity-75 "
                        size={20}
                      />
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex flex-col gap-4 pt-4">
            <StyledButton type="submit" className="w-full" loading={loading}>
              Einloggen
            </StyledButton>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default LoginInForm;
